
import { defineStore } from 'pinia'
import { useAffiliateCustomizationStore } from './affiliate-customization'
import { useAppAnalyticsStore } from '~/store/app-analytics'
import { useRootStore } from '~/store/root'
import AnalyticsService from '~/libs/analytics-service'
import env from '~/libs/env'
import find from 'lodash/find.js'
import get from 'lodash/get.js'

export const useUserStore = defineStore('user', () => {
  const appAnalyticsStore = useAppAnalyticsStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const rootStore = useRootStore()
  const nuxtApp = useNuxtApp()
  const { $axios, $lendioCookies, $pinia } = nuxtApp

  /*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const accessToken = ref(null)
  const unauthorizedToken = ref('Basic OGZkYmZlNDk4NGU5ODEwMGUzYjAwM2ViMTk1ZjA2NWM0YzY2N2U3Mzo4ZTc3MTVlMzYwNmRjZWU5Y2E2NWUzYTQ3YzM4NjFiMjFkNDAxMmY2')
  const signInError = ref(false)
  const freshLogin = ref(null)
  const borrowers = ref(null)

  /*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/

  const hasOptimusUser = computed(() => {
    return Boolean(get(rootStore.authUser, 'id'))
  })

  const hasMultipleBorrowers = computed(() => {
    return borrowers.value ? borrowers.value.length > 1 : false
  })

  /*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  async function setAccessToken(payload) {
    if (
      (payload && $lendioCookies.get('accessToken') !== payload) ||
      !accessToken.value
    ) {
      await $lendioCookies.set('accessToken', payload, {
        path: '/bp/',
        expires: 0.5,
      })
      accessToken.value = payload
    }
  }

  async function clearAccessToken() {
    accessToken.value = null
    await $lendioCookies.remove('accessToken', { path: '/bp/' })
  }

  function getAuthHeader({ type = 'BEARER', useUnauthorizedToken = false, useCookies = false, silent = true }) {

    function logNoAuthorization() {
      if (!silent) {
        log.error('Authorization from getAuthHeader is undefined')
      }
    }

    if (type === 'BEARER') {
      const token = rootStore.authToken
      const cookieToken = $lendioCookies.get('token')

      if (token) {
        return `Bearer ${token}`
      }

      if (useCookies && cookieToken) {
        return `Bearer ${cookieToken}`
      }

      if (useUnauthorizedToken) {
        return unauthorizedToken.value
      }

      logNoAuthorization()
      return false
    }

    if (type === 'OKTA_JWT') {
      const token = accessToken.value
      if (token) {
        return `JWT ${token}`
      }

      logNoAuthorization()
      return false
    }

    if (type === 'EMBEDDED_JWT') {
      const token = rootStore.embeddedJwt
      const cookieToken = $lendioCookies.get('embedded_jwt')

      if (token) {
        return `JWT ${token}`
      }

      if (useCookies && cookieToken) {
        return `JWT ${cookieToken}`
      }

      logNoAuthorization()
      return false
    }

    if (type === 'UNAUTHORIZED') {
      return unauthorizedToken.value
    }

    log.error(`No matching types for getAuthHeader: ${type}`)
    return false
  }


  async function setFreshLogin(payload) {
    if (payload) {
      freshLogin.value = payload
      await $lendioCookies.set('freshLogin', payload)
    } else {
      freshLogin.value = null
      await $lendioCookies.remove('freshLogin')
    }
  }

  /**
   * This action is fired on every mount of a layout. It should be quick,
   * and it's a catch-all for login tracking.
   */
  async function loadFreshLogin() {
    const login = freshLogin.value ?? await $lendioCookies.get('freshLogin')
    if (login == 'login') {
      await AnalyticsService.trackLogin($lendioCookies, $pinia)
    }
    this.setFreshLogin(null)
  }

  /**
   * Retrieve an optimus `access_token` if a valid `oktaId` is present.
   *
   * If `freshLogin` cookie is present as `login` it also passes marketing cookies
   * @returns
   */
  async function getAuthToken() {
    let token = rootStore.authToken
    let embeddedJwt = null
    let zendeskJwt = null
    let intercomHash = null

    if (token) {
      return
    }

    const mobileOktaIdToken = await $lendioCookies.get('mobileOktaId')
    const isMobile = await Boolean($lendioCookies.get('isMobile'))

    const login = (freshLogin.value ?? await $lendioCookies.get('freshLogin')) === 'login'
    const oktaId = rootStore.oktaId || mobileOktaIdToken

    if (!token && oktaId) {
      let loginPayload
      if (login) {
        await appAnalyticsStore.loadTrackedCookies(null)
        loginPayload = {
          cookies: appAnalyticsStore.trackedCookies,
          skipMarketingRecord: false,
        }
      } else {
        loginPayload = { skipMarketingRecord: true }
      }
      const payload = {
        oktaId: oktaId,
        isMobile: isMobile,
        ...loginPayload,
        tenantId: useAffiliateCustomizationStore().tenantId,
      }

      const loginResponse = await $axios
        .post(`${env('apiUrl')}/authorization/oktalogin`, payload)
        .then((res) => {
          return res.data.access_token ? res.data : get(res.data, 'data')
        })
        .catch((err) => null)

      if (loginResponse) {
        token = loginResponse.access_token
        embeddedJwt = loginResponse.embeddedJwt
        zendeskJwt = loginResponse.zendeskJwt
        intercomHash = loginResponse.intercomHash
      }
    }

    if (!token) return

    if (login) {
      await appAnalyticsStore.clearTrackedCookies(null)
    }

    const tokenPromises = [ rootStore.setAuthToken(token) ]

    if (embeddedJwt) {
      tokenPromises.push(rootStore.setEmbeddedToken(embeddedJwt))
    }

    if (zendeskJwt) {
      tokenPromises.push(rootStore.setZendeskEndUserToken(zendeskJwt))
    }

    if (intercomHash) {
      tokenPromises.push(rootStore.setIntercomHash(intercomHash))
    }

    await Promise.all(tokenPromises)
  }

  /**
   * When passed an Okta authorization code, this hits api to retrieve their jwt tokens
   * @param {VuexContext}
   * @param {{oktaCode: string}}
   * @returns
   */
  async function getOktaId({ oktaCode }) {
    const response = await $axios
      .post(`${env('apiUrl')}/login/okta/get-signup-tokens`, {
        code: oktaCode,
        redirectPath: 'sso-signup',
        tenantId: affiliateCustomizationStore.tenantId,
      })
      .then((res) => {
        if (res.data.response.code === 400) {
          throw res.data
        } else {
          const { data } = res.data.response
          this.setAccessToken(data.access_token)
          rootStore.setOktaId(data.id_token)
        }
        return { error: false }
      })
      .catch((err) => {
        return { error: err }
      })

    if (response.error) {
      signInError.value = true
      return response
    }

    // Get an optimus access token if the user has an oktaId
    if (rootStore.oktaId && !rootStore.authToken) {
      await this.getAuthToken()
    }
    return response
  }

  async function extractUserProfile(route) {
    const hash = get(route, 'hash', null)

    if (
      !hash ||
      hash === '#' ||
      accessToken.value ||
      rootStore.oktaId ||
      rootStore.authUser
    ) {
      return false
    }

    const parsedHash = hash
      .substr(1)
      .split('&')
      .reduce((carry, item) => {
        let [key, val] = item.split('=')
        carry[key] = val
        return carry
      }, {})

    if (!parsedHash.access_token || !parsedHash.id_token) {
      return false
    }

    await this.setAccessToken(parsedHash.access_token)
    await rootStore.setOktaId(parsedHash.id_token)
    route.hash = ''
    route.fullPath = route.path
    route.href = route.path
    return route
  }

  async function getFactorId(retries = 1) {
    let emailId = null

    await $axios
      .post(`${env('apiUrl')}/authorization/factors`, {
        oktaUserId: rootStore.authUser.oktaUserId,
      })
      .then((res) => {
        if (res.data.errorSummary) {
          error.value =
            "There was a problem verifying your factor enrollment. We'll ask you to  verify at a later time."
        } else {
          let email = find(res.data, function (o) {
            return o.factorType == 'email'
          })
          // If no email object, return undefined/false.
          emailId = (email || {}).id
        }
      })

    return emailId || (retries > 0 && (await this.getFactorId(retries - 1)))
  }

  /**
   * Starts email multi-factor verification by telling Okta to send (email)
   * the user a verification passcode. The user will enter this code from
   * the message to complete the process via `verifyEmailFactorPasscode`
   * below.
   *
   * See usage in verify-email.vue and EmailVerificationModal.vue.
   *
   * @param {string} emailFactorId Id for the factor (email in this case)
   * @returns {object} Json response with error or okta result
   */
  async function sendEmailFactorPasscode({ emailFactorId }) {
    const params = {
      emailFactorId,
      oktaUserId: rootStore.authUser.oktaUserId,
    }

    // Centralize all error handling here in the store action.
    return await $axios
      .post(`${env('apiUrl')}/authorization/email-verification`, params)
      .then((res) => {
        if (res.data && res.data.errorCode && res.data.errorSummary) {
          // Error.
          switch (res.data.errorCode) {
            // See okta error codes at https://developer.okta.com/docs/reference/error-codes.
            case 'E0000069':
              // Okta errorSummary: User Locked
              return {
                error: 'Your account has been temporarily locked',
                redirect: `${affiliateCustomizationStore.oktaBaseUrl}/signin/unlock?ev=1`,
              }

            case 'E0000118':
              // Okta errorSummary: An email was recently sent. Please wait 5 seconds before trying again.
              return {
                error:
                  'An email with a verification code was sent. Please wait five seconds before trying again.',
                alertMethod: 'openWarning',
              }
            default:
              // Other errors for which we aren't checking specific codes.
              log.error(
                new Error('Failed to send an okta validation email'),
                res.data
              )
              return {
                error: 'Failed to send a validation email',
                alertMethod: 'openDanger',
              }
          }
        } else {
          // Success.
          return res
        }
      })
      .catch((err) => {
        // Lendio api errors, not okta (handled above).
        return {
          error:
            'There was an error trying to verify your email. Please try again.',
        }
      })
  }

  /**
   * Confirm email multi-factor verification with pass code emailed to user
   * from Okta. Passing this completes the email MFA verification process.
   *
   * See usage in verify-email.vue and EmailVerificationModal.vue.
   *
   * @param {number} emailFactorId Id for the factor (email in this case)
   * @param {string} code Pass code from Okta
   * @returns {object} Json response with error or okta result
   */
  async function verifyEmailFactorPasscode({ emailFactorId, code }) {
    const params = {
      emailFactorId,
      oktaUserId: rootStore.authUser.oktaUserId,
      code,
    }

    // Centralize all error handling here in the store action.
    return await $axios
      .post(`${env('apiUrl')}/authorization/email-verification`, params)
      .then((res) => {
        if (res.data.errorCode && res.data.errorSummary) {
          // Error.
          switch (res.data.errorCode) {
            // See okta error codes at https://developer.okta.com/docs/reference/error-codes.

            case 'E0000068':
              // Okta errorSummary: Invalid Passcode/Answer
              return {
                error: 'The code you entered is incorrect. Please try again.',
              }

            case 'E0000069':
              // Okta errorSummary: User Locked
              return {
                error: 'Your account has been locked',
                redirect: `${affiliateCustomizationStore.oktaBaseUrl}/signin/unlock?ev=1`,
              }

            default:
              // Other errors for which we aren't checking specific codes.
              log.error(
                new Error(
                  'Failed to validate okta email verification pass code'
                ),
                res.data
              )
              return {
                error: 'Failed to validate your email confirmation code',
                alertMethod: 'openDanger',
              }
          }
        } else {
          // Success.
          return res
        }
      })
      .catch((err) => {
        // Lendio api errors, not okta (handled above).
        return {
          error:
            'There was an error trying to verify your email. Please try again.',
        }
      })
  }

  async function getBorrowers(force = false) {
    const currentUser = get(rootStore.authUser, 'id')
      ? rootStore.authUser
      : await rootStore.getCurrentUser({
          bypassRedirect: true,
        })

    if (!currentUser || typeof currentUser.id === 'undefined') return
    if (borrowers.value && force === false) return

    let res = await $axios
      .get(`${env('apiUrl')}/user/${get(currentUser, 'id')}/borrowers`)
      .catch((e) => {
        if (e.message !== 'Request failed with status code 401') {
          log.error('Unable to get borrowers', {
            err: e,
            errMessage: e.message,
            userId: currentUser.id || null,
            forceReload,
          })
        }
      })
    const data = get(res, 'data.data')
    if (!data) throw new Error('No data returned from user borrowers endpoint')
    const _borrowers = data
    borrowers.value = _borrowers
    return _borrowers
  }

  function setBorrowers(_borrowers) {
    borrowers.value = _borrowers
  }

  async function reauth({ force = false } = {}) {
    const now = Date.now()
    if (!force) {
      const lastRunReauth = $lendioCookies.get('lastRunReauth')
      // Only run if has been greater than 5 minutes since last reauth run
      if (now - lastRunReauth < 300000) {
        return
      }
    }

    const currentUser = get(rootStore.authUser, 'id')
      ? rootStore.authUser
      : await rootStore.getCurrentUser({
          bypassRedirect: true,
        })
    if (!currentUser || typeof currentUser.id === 'undefined') return

    let res = await $axios
      .put(`${env('apiUrl')}/user/${get(currentUser, 'id')}/bp-re-auth`)
      .catch((e) => {
        if (e.message !== 'Request failed with status code 401') {
          log.error('Unable to reauth', {
            err: e,
            errMessage: e.message,
            userId: currentUser.id || null,
            forceReload,
          })
        }
      })

    const responseData = get(res, 'data.data')
    if (!responseData)
      throw new Error('No data returned from user reauth endpoint')
    if (responseData == get(currentUser, 'id')) {
      $lendioCookies.set('lastRunReauth', now)
      return true
    } else {
      return false
    }
  }

  return {
    //STATE
    accessToken,
    signInError,
    freshLogin,
    borrowers,
    unauthorizedToken,

    //GETTERS
    hasOptimusUser,
    hasMultipleBorrowers,

    //ACTIONS
    setAccessToken,
    setBorrowers,
    clearAccessToken,
    getAuthHeader,
    setFreshLogin,
    loadFreshLogin,
    getAuthToken,
    getOktaId,
    extractUserProfile,
    getFactorId,
    sendEmailFactorPasscode,
    verifyEmailFactorPasscode,
    getBorrowers,
    reauth,
  }
})
